import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { api } from '../services/api';
import { applyAppearanceToRoot, resetRootAppearance } from './rootAppearance';
import {
  DEFAULT_APPEARANCE,
  normalizeAppearance,
  type AppearanceKey,
  type AppearanceSettings,
} from './types';

interface AppearanceContextValue {
  settings: AppearanceSettings;
  loading: boolean;
  loadError: string | null;
  fieldErrors: Partial<Record<AppearanceKey, string>>;
  saving: Partial<Record<AppearanceKey, boolean>>;
  saved: Partial<Record<AppearanceKey, boolean>>;
  updateAppearance<K extends AppearanceKey>(key: K, value: AppearanceSettings[K]): void;
  retryAppearance(): void;
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

const cloneDefaults = (): AppearanceSettings => ({ ...DEFAULT_APPEARANCE });
const keys: AppearanceKey[] = ['theme', 'accent', 'font_size', 'density', 'motion'];
const SAVED_MESSAGE_DURATION_MS = 1500;

type PendingSave = {
  generation: number;
  ownerGeneration: number;
  value: AppearanceSettings[AppearanceKey];
};

const readSystemPreferences = () => ({
  dark: window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false,
  reduced: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
});

export const AppearanceProvider = ({ userId, children }: { userId: string; children: ReactNode }) => {
  const [settings, setSettings] = useState<AppearanceSettings>(cloneDefaults);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<AppearanceKey, string>>>({});
  const [saving, setSaving] = useState<Partial<Record<AppearanceKey, boolean>>>({});
  const [saved, setSaved] = useState<Partial<Record<AppearanceKey, boolean>>>({});
  const [systemPreferences, setSystemPreferences] = useState(readSystemPreferences);
  const settingsRef = useRef(settings);
  const confirmedRef = useRef<AppearanceSettings>(cloneDefaults());
  const fieldGenerationsRef = useRef<Record<AppearanceKey, number>>({
    theme: 0, accent: 0, font_size: 0, density: 0, motion: 0,
  });
  const timeoutsRef = useRef<Partial<Record<AppearanceKey, ReturnType<typeof setTimeout>>>>({});
  const savedTimeoutsRef = useRef<Partial<Record<AppearanceKey, ReturnType<typeof setTimeout>>>>({});
  const queuedSavesRef = useRef<Partial<Record<AppearanceKey, PendingSave>>>({});
  const inFlightSavesRef = useRef<Partial<Record<AppearanceKey, PendingSave>>>({});
  const ownerGenerationRef = useRef(0);
  const loadGenerationRef = useRef(0);
  const mountedRef = useRef(true);

  const clearTimeouts = useCallback(() => {
    for (const key of keys) {
      const timeout = timeoutsRef.current[key];
      if (timeout) clearTimeout(timeout);
      const savedTimeout = savedTimeoutsRef.current[key];
      if (savedTimeout) clearTimeout(savedTimeout);
    }
    timeoutsRef.current = {};
    savedTimeoutsRef.current = {};
  }, []);

  const drainField = useCallback(function drain(key: AppearanceKey): void {
    if (inFlightSavesRef.current[key]) return;
    const request = queuedSavesRef.current[key];
    if (!request) return;

    delete queuedSavesRef.current[key];
    inFlightSavesRef.current[key] = request;
    void api.patch('/profile/appearance', { [key]: request.value })
      .then((response) => {
        const normalized = normalizeAppearance(response.data);
        if (!normalized) throw new Error('Malformed appearance response');
        if (
          !mountedRef.current
          || request.ownerGeneration !== ownerGenerationRef.current
        ) return;

        confirmedRef.current = { ...confirmedRef.current, [key]: normalized[key] };
        if (request.generation !== fieldGenerationsRef.current[key]) return;

        const next = { ...settingsRef.current, [key]: normalized[key] };
        settingsRef.current = next;
        setSettings(next);
        setSaving((current) => ({ ...current, [key]: false }));
        setSaved((current) => ({ ...current, [key]: true }));
        const previousSavedTimeout = savedTimeoutsRef.current[key];
        if (previousSavedTimeout) clearTimeout(previousSavedTimeout);
        savedTimeoutsRef.current[key] = setTimeout(() => {
          if (
            mountedRef.current
            && request.ownerGeneration === ownerGenerationRef.current
            && request.generation === fieldGenerationsRef.current[key]
          ) setSaved((current) => ({ ...current, [key]: false }));
        }, SAVED_MESSAGE_DURATION_MS);
      })
      .catch(() => {
        if (
          !mountedRef.current
          || request.ownerGeneration !== ownerGenerationRef.current
          || request.generation !== fieldGenerationsRef.current[key]
        ) return;

        const next = { ...settingsRef.current, [key]: confirmedRef.current[key] };
        settingsRef.current = next;
        setSettings(next);
        setSaving((current) => ({ ...current, [key]: false }));
        setSaved((current) => ({ ...current, [key]: false }));
        setFieldErrors((current) => ({ ...current, [key]: 'Не удалось сохранить настройку оформления' }));
      })
      .finally(() => {
        if (inFlightSavesRef.current[key] !== request) return;
        delete inFlightSavesRef.current[key];
        if (
          mountedRef.current
          && request.ownerGeneration === ownerGenerationRef.current
        ) drain(key);
      });
  }, []);

  const loadAppearance = useCallback(async () => {
    const ownerGeneration = ownerGenerationRef.current;
    const loadGeneration = ++loadGenerationRef.current;
    const fieldGenerationsAtLoad = { ...fieldGenerationsRef.current };
    if (mountedRef.current) {
      setLoading(true);
      setLoadError(null);
    }

    try {
      const response = await api.get('/profile/appearance');
      const normalized = normalizeAppearance(response.data);
      if (!normalized) throw new Error('Malformed appearance response');
      if (
        !mountedRef.current
        || ownerGeneration !== ownerGenerationRef.current
        || loadGeneration !== loadGenerationRef.current
      ) return;
      const nextSettings = { ...settingsRef.current };
      const nextConfirmed = { ...confirmedRef.current };
      for (const key of keys) {
        if (fieldGenerationsAtLoad[key] !== fieldGenerationsRef.current[key]) continue;
        Object.assign(nextSettings, { [key]: normalized[key] });
        Object.assign(nextConfirmed, { [key]: normalized[key] });
      }
      confirmedRef.current = nextConfirmed;
      settingsRef.current = nextSettings;
      setSettings(nextSettings);
    } catch {
      if (
        !mountedRef.current
        || ownerGeneration !== ownerGenerationRef.current
        || loadGeneration !== loadGenerationRef.current
      ) return;
      setLoadError('Не удалось загрузить настройки оформления');
    } finally {
      if (
        mountedRef.current
        && ownerGeneration === ownerGenerationRef.current
        && loadGeneration === loadGenerationRef.current
      ) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const darkMedia = window.matchMedia?.('(prefers-color-scheme: dark)');
    const reducedMedia = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const updatePreferences = () => setSystemPreferences(readSystemPreferences());
    darkMedia?.addEventListener('change', updatePreferences);
    reducedMedia?.addEventListener('change', updatePreferences);
    return () => {
      darkMedia?.removeEventListener('change', updatePreferences);
      reducedMedia?.removeEventListener('change', updatePreferences);
    };
  }, []);

  useEffect(() => {
    applyAppearanceToRoot(settings, systemPreferences.dark, systemPreferences.reduced);
  }, [settings, systemPreferences]);

  useEffect(() => {
    mountedRef.current = true;
    ownerGenerationRef.current += 1;
    clearTimeouts();
    const defaults = cloneDefaults();
    settingsRef.current = defaults;
    confirmedRef.current = defaults;
    fieldGenerationsRef.current = { theme: 0, accent: 0, font_size: 0, density: 0, motion: 0 };
    queuedSavesRef.current = {};
    inFlightSavesRef.current = {};
    setSettings(defaults);
    setFieldErrors({});
    setSaving({});
    setSaved({});
    void loadAppearance();

    return () => {
      ownerGenerationRef.current += 1;
      loadGenerationRef.current += 1;
      clearTimeouts();
      queuedSavesRef.current = {};
      inFlightSavesRef.current = {};
      resetRootAppearance();
    };
  }, [clearTimeouts, loadAppearance, userId]);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const updateAppearance = useCallback(<K extends AppearanceKey>(key: K, value: AppearanceSettings[K]) => {
    const generation = fieldGenerationsRef.current[key] + 1;
    fieldGenerationsRef.current[key] = generation;
    delete queuedSavesRef.current[key];
    const ownerGeneration = ownerGenerationRef.current;
    const previousTimeout = timeoutsRef.current[key];
    if (previousTimeout) clearTimeout(previousTimeout);
    const previousSavedTimeout = savedTimeoutsRef.current[key];
    if (previousSavedTimeout) clearTimeout(previousSavedTimeout);

    const nextSettings = { ...settingsRef.current, [key]: value } as AppearanceSettings;
    settingsRef.current = nextSettings;
    setSettings(nextSettings);
    setFieldErrors((current) => ({ ...current, [key]: undefined }));
    setSaving((current) => ({ ...current, [key]: true }));
    setSaved((current) => ({ ...current, [key]: false }));

    timeoutsRef.current[key] = setTimeout(() => {
      delete timeoutsRef.current[key];
      queuedSavesRef.current[key] = { generation, ownerGeneration, value };
      drainField(key);
    }, 250);
  }, [drainField]);

  const value = useMemo<AppearanceContextValue>(() => ({
    settings,
    loading,
    loadError,
    fieldErrors,
    saving,
    saved,
    updateAppearance,
    retryAppearance: () => { void loadAppearance(); },
  }), [fieldErrors, loadAppearance, loadError, loading, saved, saving, settings, updateAppearance]);

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
};

export const useAppearance = (): AppearanceContextValue => {
  const value = useContext(AppearanceContext);
  if (!value) throw new Error('useAppearance must be used inside AppearanceProvider');
  return value;
};
